import { Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";

import PublicLayout from "@/components/layout/PublicLayout";
import StudentLayout from "@/components/layout/StudentLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import LoadingScreen from "@/components/common/LoadingScreen";
import RequireAuth from "@/features/auth/RequireAuth";
import AuthInit from "@/features/auth/AuthInit";
import { useApplyFavicon } from "@/lib/useApplyFavicon";
import { useSpaPageViews, useTrackingScripts } from "@/lib/useTrackingScripts";

// Public
const HomePage = lazy(() => import("@/routes/public/HomePage"));
const AboutPage = lazy(() => import("@/routes/public/AboutPage"));
const CoursesPage = lazy(() => import("@/routes/public/CoursesPage"));
const CourseDetailPage = lazy(() => import("@/routes/public/CourseDetailPage"));
const PackagesPage = lazy(() => import("@/routes/public/PackagesPage"));
const PackageDetailPage = lazy(() => import("@/routes/public/PackageDetailPage"));
const JspsPillarPage = lazy(() => import("@/routes/public/JspsPillarPage"));
const ContactPage = lazy(() => import("@/routes/public/ContactPage"));
const BlogListPage = lazy(() => import("@/routes/public/BlogListPage"));
const JspsCikmisSorularBlogPage = lazy(() => import("@/routes/public/JspsCikmisSorularBlogPage"));
const JspsUzmanCavusAstsubayGecisBlogPage = lazy(
  () => import("@/routes/public/JspsUzmanCavusAstsubayGecisBlogPage")
);
const BlogPostPage = lazy(() => import("@/routes/public/BlogPostPage"));
const LegalPage = lazy(() => import("@/routes/public/LegalPage"));
const CartPage = lazy(() => import("@/routes/public/CartPage"));
const CheckoutPage = lazy(() => import("@/routes/public/CheckoutPage"));
const OrderPaymentPage = lazy(() => import("@/routes/public/OrderPaymentPage"));
const OrderSuccessPage = lazy(() => import("@/routes/public/OrderSuccessPage"));
const OrderFailPage = lazy(() => import("@/routes/public/OrderFailPage"));
const LoginPage = lazy(() => import("@/routes/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/routes/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/routes/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/routes/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/routes/auth/VerifyEmailPage"));
const NotFoundPage = lazy(() => import("@/routes/public/NotFoundPage"));
const FreeQuizzesPage = lazy(() => import("@/routes/public/FreeQuizzesPage"));
const FreeQuizPage = lazy(() => import("@/routes/public/FreeQuizPage"));
const BooksPage = lazy(() => import("@/routes/public/BooksPage"));
const BookDetailPage = lazy(() => import("@/routes/public/BookDetailPage"));

// Student
const DashboardPage = lazy(() => import("@/routes/student/DashboardPage"));
const MyCoursesPage = lazy(() => import("@/routes/student/MyCoursesPage"));
const MyCoursePage = lazy(() => import("@/routes/student/MyCoursePage"));
const LessonPage = lazy(() => import("@/routes/student/LessonPage"));
const MyOrdersPage = lazy(() => import("@/routes/student/MyOrdersPage"));
const ProfilePage = lazy(() => import("@/routes/student/ProfilePage"));
const MyBooksPage = lazy(() => import("@/routes/student/MyBooksPage"));

// Admin
const AdminDashboardPage = lazy(() => import("@/routes/admin/AdminDashboardPage"));
const AdminCoursesPage = lazy(() => import("@/routes/admin/AdminCoursesPage"));
const AdminCourseFormPage = lazy(() => import("@/routes/admin/AdminCourseFormPage"));
const AdminCourseContentPage = lazy(() => import("@/routes/admin/AdminCourseContentPage"));
const AdminPackagesPage = lazy(() => import("@/routes/admin/AdminPackagesPage"));
const AdminPackageFormPage = lazy(() => import("@/routes/admin/AdminPackageFormPage"));
const AdminUsersPage = lazy(() => import("@/routes/admin/AdminUsersPage"));
const AdminOrdersPage = lazy(() => import("@/routes/admin/AdminOrdersPage"));
const AdminShippingPage = lazy(() => import("@/routes/admin/AdminShippingPage"));
const AdminCouponsPage = lazy(() => import("@/routes/admin/AdminCouponsPage"));
const AdminAnnouncementsPage = lazy(() => import("@/routes/admin/AdminAnnouncementsPage"));
const AdminLiveSessionsPage = lazy(() => import("@/routes/admin/AdminLiveSessionsPage"));
const AdminBlogPage = lazy(() => import("@/routes/admin/AdminBlogPage"));
const AdminBlogEditorPage = lazy(() => import("@/routes/admin/AdminBlogEditorPage"));
const AdminSettingsPage = lazy(() => import("@/routes/admin/AdminSettingsPage"));
const AdminAppManagementPage = lazy(() => import("@/routes/admin/AdminAppManagementPage"));
const AdminProfilePage = lazy(() => import("@/routes/admin/AdminProfilePage"));
const AdminLegalPagesPage = lazy(() => import("@/routes/admin/AdminLegalPagesPage"));
const AdminEmailTemplatesPage = lazy(() => import("@/routes/admin/AdminEmailTemplatesPage"));
const AdminTestimonialsPage = lazy(() => import("@/routes/admin/AdminTestimonialsPage"));
const AdminContactMessagesPage = lazy(() => import("@/routes/admin/AdminContactMessagesPage"));
const AdminHeroSlidesPage = lazy(() => import("@/routes/admin/AdminHeroSlidesPage"));
const AdminFreeQuizzesPage = lazy(() => import("@/routes/admin/AdminFreeQuizzesPage"));
const AdminFreeQuizDetailPage = lazy(() => import("@/routes/admin/AdminFreeQuizDetailPage"));
const AdminFreeQuizAttemptsPage = lazy(() => import("@/routes/admin/AdminFreeQuizAttemptsPage"));
const AdminFreeQuizAttemptDetailPage = lazy(
  () => import("@/routes/admin/AdminFreeQuizAttemptDetailPage")
);
const AIBookletPage = lazy(() => import("@/routes/admin/AIBookletPage"));
const AdminBookletPdfEditorPage = lazy(() => import("@/routes/admin/AdminBookletPdfEditorPage"));
const AdminBooksPage = lazy(() => import("@/routes/admin/AdminBooksPage"));
const AdminBookFormPage = lazy(() => import("@/routes/admin/AdminBookFormPage"));
const AILectureNotesPage = lazy(() => import("@/routes/admin/AILectureNotesPage"));
const JSPSGroundedLecturePage = lazy(
  () => import("@/routes/admin/JSPSGroundedLecturePage")
);
const AdminVideoAutomationPage = lazy(
  () => import("@/routes/admin/AdminVideoAutomationPage")
);

export default function App() {
  useApplyFavicon();
  useTrackingScripts();
  useSpaPageViews();
  return (
    <>
      {/* AuthInit tüm route'lardan önce — hem public hem korumalı sayfalarda çalışır */}
      <AuthInit />
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="jsps-sinavi" element={<JspsPillarPage />} />
          <Route path="hakkimizda" element={<AboutPage />} />
          <Route path="egitimler" element={<CoursesPage />} />
          <Route path="egitimler/:slug" element={<CourseDetailPage />} />
          <Route path="paketler" element={<PackagesPage />} />
          <Route path="paketler/:slug" element={<PackageDetailPage />} />
          <Route path="kitaplar" element={<BooksPage />} />
          <Route path="kitaplar/:slug" element={<BookDetailPage />} />
          <Route path="iletisim" element={<ContactPage />} />
          <Route path="blog" element={<BlogListPage />} />
          <Route path="blog/jsps-cikmis-sorular" element={<JspsCikmisSorularBlogPage />} />
          <Route
            path="blog/jsps-uzman-cavus-astsubay-gecis-ve-branslar"
            element={<JspsUzmanCavusAstsubayGecisBlogPage />}
          />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="kvkk-aydinlatma" element={<LegalPage slug="kvkk" />} />
          <Route path="mesafeli-satis-sozlesmesi" element={<LegalPage slug="mesafeli" />} />
          <Route path="iptal-iade-kosullari" element={<LegalPage slug="iade" />} />
          <Route path="gizlilik-politikasi" element={<LegalPage slug="gizlilik" />} />
          <Route path="cerez-politikasi" element={<LegalPage slug="cerez" />} />
          <Route path="sepet" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="odeme/:merchantOid" element={<OrderPaymentPage />} />
          <Route path="odeme-basarili" element={<OrderSuccessPage />} />
          <Route path="odeme-basarisiz" element={<OrderFailPage />} />
          <Route path="giris" element={<LoginPage />} />
          <Route path="kayit" element={<RegisterPage />} />
          <Route path="sifremi-unuttum" element={<ForgotPasswordPage />} />
          <Route path="sifre-sifirla/:token" element={<ResetPasswordPage />} />
          <Route path="eposta-dogrula/:token" element={<VerifyEmailPage />} />
          <Route path="ucretsiz-quizler" element={<FreeQuizzesPage />} />
          <Route path="ucretsiz-quizler/:slug" element={<FreeQuizPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Student panel - auth required */}
        <Route element={<RequireAuth />}>
          <Route path="/panel" element={<StudentLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="kurslarim" element={<MyCoursesPage />} />
            <Route path="denemeler" element={<FreeQuizzesPage />} />
            <Route path="kurs/:slug" element={<MyCoursePage />} />
            <Route path="ders/:lessonId" element={<LessonPage />} />
            <Route path="siparislerim" element={<MyOrdersPage />} />
            <Route path="kitaplarim" element={<MyBooksPage />} />
            <Route path="profil" element={<ProfilePage />} />
          </Route>
        </Route>

        {/* Admin panel - ADMIN role required */}
        <Route element={<RequireAuth roles={["ADMIN"]} />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="kurslar" element={<AdminCoursesPage />} />
            <Route path="kurslar/yeni" element={<AdminCourseFormPage />} />
            <Route path="kurslar/:id" element={<AdminCourseFormPage />} />
            <Route path="kurslar/:id/icerik" element={<AdminCourseContentPage />} />
            <Route path="paketler" element={<AdminPackagesPage />} />
            <Route path="paketler/yeni" element={<AdminPackageFormPage />} />
            <Route path="paketler/:id" element={<AdminPackageFormPage />} />
            <Route path="kitaplar" element={<AdminBooksPage />} />
            <Route path="kitaplar/:id" element={<AdminBookFormPage />} />
            <Route path="canli-dersler" element={<AdminLiveSessionsPage />} />
            <Route path="kullanicilar" element={<AdminUsersPage />} />
            <Route path="siparisler" element={<AdminOrdersPage />} />
            <Route path="kargo" element={<AdminShippingPage />} />
            <Route path="indirim-kodlari" element={<AdminCouponsPage />} />
            <Route path="iletisim" element={<AdminContactMessagesPage />} />
            <Route path="blog" element={<AdminBlogPage />} />
            <Route path="blog/yeni" element={<AdminBlogEditorPage />} />
            <Route path="blog/:id" element={<AdminBlogEditorPage />} />
            <Route path="anasayfa-slayt" element={<AdminHeroSlidesPage />} />
            <Route path="duyurular" element={<AdminAnnouncementsPage />} />
            <Route path="yorumlar" element={<AdminTestimonialsPage />} />
            <Route path="yasal" element={<AdminLegalPagesPage />} />
            <Route path="email-sablonlari" element={<AdminEmailTemplatesPage />} />
            <Route path="ayarlar" element={<AdminSettingsPage />} />
            <Route path="uygulama" element={<AdminAppManagementPage />} />
            <Route path="profilim" element={<AdminProfilePage />} />
            <Route path="ucretsiz-quizler" element={<AdminFreeQuizzesPage />} />
            <Route path="ucretsiz-quizler/:id/pdf-duzenle" element={<AdminBookletPdfEditorPage />} />
            <Route path="ucretsiz-quizler/:id" element={<AdminFreeQuizDetailPage />} />
            <Route path="deneme-sonuclari" element={<AdminFreeQuizAttemptsPage />} />
            <Route
              path="deneme-sonuclari/:attemptId"
              element={<AdminFreeQuizAttemptDetailPage />}
            />
            <Route path="ai-kitapcik" element={<AIBookletPage />} />
            <Route path="ai-kitapcik/harici-pdf" element={<AdminBookletPdfEditorPage />} />
            <Route path="ai-konu-anlatimi" element={<AILectureNotesPage />} />
            <Route
              path="jsps-grounded-konu-anlatimi"
              element={<JSPSGroundedLecturePage />}
            />
            <Route path="video-otomasyon" element={<AdminVideoAutomationPage />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}
